<template>
    <DashedCard :margin-top="37" :margin-bottom="37">
        <template #title>
            <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                <BaseSwitch v-model="isPersonalized" />
                <span class="poppinsRegular ppv2-text-black" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ $t('experience.step4.reservationPeriods.title') }}
                </span>
            </div>
        </template>

        <template #content>
            <!-- Day Preview (always visible) -->
            <div class="ppv2-px-4 ppv2-pt-4 ppv2-mb-4">
                <div class="ppv2-flex ppv2-justify-between ppv2-items-end ppv2-my-2 ppv2-pt-2">
                    <span
                        class="poppinsMedium text-purple ppv2-text-opacity-100 ppv2-uppercase"
                        :style="{ fontSize: normalizeFontSize(12) }"
                    >
                        {{ $t('experience.step4.reservationPeriods.dayPreview') }}
                    </span>
                </div>

                <!-- Timeline Labels -->
                <div class="ppv2-flex ppv2-justify-between ppv2-mb-1 ppv2-px-1">
                    <span
                        v-for="hour in ['8h', '12h', '16h', '20h', '22h']"
                        :key="hour"
                        class="poppinsLight ppv2-text-[#9CA3AF]"
                        :style="{ fontSize: normalizeFontSize(11) }"
                    >
                        {{ hour }}
                    </span>
                </div>

                <!-- Progress Bar -->
                <div
                    class="ppv2-h-2 ppv2-w-full ppv2-bg-black ppv2-bg-opacity-20 ppv2-rounded-full ppv2-relative ppv2-overflow-hidden"
                >
                    <template v-if="isPersonalized">
                        <div
                            v-for="segment in filledSegments"
                            :key="segment.id"
                            class="ppv2-absolute ppv2-h-full ppv2-rounded-[8px]"
                            :style="{
                                left: segment.left + '%',
                                width: segment.width + '%',
                                background: 'linear-gradient(90deg, #8241D7 0%, #3A329D 100%)',
                            }"
                        ></div>
                    </template>
                </div>
            </div>

            <div v-if="isPersonalized" class="ppv2-px-4 ppv2-pb-4">
                <!-- Empty State -->
                <div v-if="!periods.length && !loading && !showAddForm" class="ppv2-text-center ppv2-py-8">
                    <p class="poppinsLight ppv2-text-black ppv2-text-opacity-80" :style="{ fontSize: normalizeFontSize(13) }">
                        {{ $t('experience.step4.reservationPeriods.noPeriods') }}
                    </p>
                </div>

                <!-- Periods List -->
                <div v-else class="ppv2-space-y-3 ppv2-mb-6">
                    <div
                        v-for="(period, index) in periods"
                        :key="period.id"
                        class="ppv2-flex ppv2-items-center ppv2-p-3 ppv2-border ppv2-border-[#545595] ppv2-rounded-[12px]"
                        :style="{ gap: normalizeSize(16, true) }"
                    >
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-flex-1">
                            <Clock :size="16" class="ppv2-text-[#C4B5FD] ppv2-shrink-0" />
                            <BaseTimeInput
                                :value="formatTime(period.start)"
                                @input="
                                    period.start = $event
                                    updatePeriod(period)
                                "
                                inputClass="!ppv2-min-h-[32px] !ppv2-h-[32px] ppv2-max-w-[105px] !ppv2-rounded-lg !ppv2-text-sm"
                            />
                            <span class="ppv2-text-[#C4B5FD]">→</span>
                            <BaseTimeInput
                                :value="formatTime(period.end)"
                                :minTime="formatTime(period.start)"
                                @input="
                                    period.end = $event
                                    updatePeriod(period)
                                "
                                inputClass="!ppv2-min-h-[32px] !ppv2-h-[32px] ppv2-max-w-[105px] !ppv2-rounded-lg !ppv2-text-sm"
                            />
                        </div>

                        <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                            <span
                                class="poppinsLight ppv2-text-black ppv2-text-opacity-60"
                                :style="{ fontSize: normalizeFontSize(12) }"
                            >
                                {{ $t('experience.step4.reservationPeriods.active') }}
                            </span>
                            <BaseSwitch :modelValue="!!period.active" @input="toggleActive(period)" />
                        </div>

                        <button
                            @click="deletePeriod(period.id)"
                            class="ppv2-text-black ppv2-bg-black ppv2-bg-opacity-10 ppv2-rounded-full ppv2-h-[26px] ppv2-w-[26px] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-opacity-60 hover:ppv2-text-opacity-100 ppv2-p-1"
                        >
                            <X :size="18" />
                        </button>
                    </div>
                </div>

                <!-- Add Form -->
                <div
                    v-if="showAddForm"
                    class="ppv2-flex ppv2-items-center ppv2-p-3 ppv2-border ppv2-border-[#545595] ppv2-border-dashed ppv2-rounded-[12px] ppv2-mb-4"
                    :style="{ gap: normalizeSize(16, true) }"
                >
                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-flex-1">
                        <Clock :size="16" class="ppv2-text-[#C4B5FD] ppv2-text-opacity-60 ppv2-shrink-0" />
                        <BaseTimeInput
                            :value="form.start"
                            :placeholder="$t('experience.step4.reservationPeriods.startTime')"
                            @input="form.start = $event"
                            inputClass="!ppv2-min-h-[32px] !ppv2-h-[32px] ppv2-max-w-[170px] !ppv2-rounded-lg !ppv2-text-sm"
                        />
                        <span class="ppv2-text-[#C4B5FD] ppv2-text-opacity-60">→</span>
                        <BaseTimeInput
                            :value="form.end"
                            :minTime="form.start"
                            :placeholder="$t('experience.step4.reservationPeriods.endTime')"
                            @input="form.end = $event"
                            inputClass="!ppv2-min-h-[32px] !ppv2-h-[32px] ppv2-max-w-[170px] !ppv2-rounded-lg !ppv2-text-sm"
                        />
                    </div>
                    <div class="ppv2-flex ppv2-gap-2">
                        <button
                            @click="addPeriod"
                            :disabled="!isFormValid || actionLoading"
                            class="ppv2-text-black hover:ppv2-text-[#7D2DBD]"
                        >
                            <Check :size="20" class="ppv2-cursor-pointer" />
                        </button>
                        <button
                            @click="showAddForm = false"
                            class="ppv2-text-black ppv2-text-opacity-60 hover:ppv2-text-opacity-100 ppv2-cursor-pointer"
                        >
                            <X :size="20" />
                        </button>
                    </div>
                </div>

                <!-- Add Button -->
                <button
                    v-else
                    @click="showAddForm = true"
                    class="ppv2-w-full ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-2 ppv2-py-3 ppv2-bg-black ppv2-bg-opacity-5 ppv2-border ppv2-border-[#545595] ppv2-border-solid ppv2-rounded-[12px] ppv2-text-black poppinsRegular hover:ppv2-bg-opacity-10 ppv2-transition-all"
                    :style="{ fontSize: normalizeFontSize(13) }"
                >
                    <Plus :size="16" />
                    {{ $t('experience.step4.reservationPeriods.add') }}
                </button>

                <!-- Validation Error Inline -->
                <p v-if="validationError" class="ppv2-text-[#ed4b9e] ppv2-text-xs ppv2-mt-2 ppv2-text-center">
                    {{ validationError }}
                </p>
            </div>
        </template>
    </DashedCard>
</template>

<script>
import { Clock, Plus, X, Check } from 'lucide-vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import BaseTimeInput from '../../../Shared/BaseTimeInput.vue'
import ApiManager from '../../../Calendar/_core/ApiManager.js'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'ReservationPeriods',
    components: {
        BaseSwitch,
        DashedCard,
        BaseTimeInput,
        Clock,
        Plus,
        X,
        Check,
    },
    props: {
        prestationId: {
            type: [Number, String],
            required: true,
        },
    },
    data() {
        return {
            periods: [],
            loading: false,
            actionLoading: false,
            isPersonalized: true,
            showAddForm: false,
            form: {
                start: '',
                end: '',
                active: true,
            },
            alertMessage: '',
            alertType: '',
            validationError: '',
        }
    },
    computed: {
        isFormValid() {
            if (!this.form.start || !this.form.end) return false
            return this.form.start < this.form.end
        },
        filledSegments() {
            if (!this.isPersonalized) return []
            return this.periods
                .filter((p) => {
                    const val = p.active
                    return val === true || val === 1 || val === '1' || val === 'true'
                })
                .map((p) => {
                    const left = this.minutesToPercentage(this.timeToMinutes(p.start))
                    const right = this.minutesToPercentage(this.timeToMinutes(p.end))

                    return {
                        id: p.id,
                        left: Math.max(0, left),
                        width: Math.max(0, right - left),
                    }
                })
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        minutesToPercentage(mins) {
            // Non-linear mapping to match evenly spaced labels:
            // 8:00 (480) -> 0%
            // 12:00 (720) -> 25%
            // 16:00 (960) -> 50%
            // 20:00 (1200) -> 75%
            // 22:00 (1320) -> 100%

            if (mins <= 480) return 0
            if (mins >= 1320) return 100

            if (mins <= 720) {
                return ((mins - 480) / 240) * 25
            } else if (mins <= 960) {
                return 25 + ((mins - 720) / 240) * 25
            } else if (mins <= 1200) {
                return 50 + ((mins - 960) / 240) * 25
            } else {
                return 75 + ((mins - 1200) / 120) * 25
            }
        },
        timeToMinutes(time) {
            if (!time) return 0
            const [hours, minutes] = time.split(':').map(Number)
            return hours * 60 + minutes
        },
        formatTime(time) {
            if (!time) return ''
            return time.substring(0, 5)
        },
        async fetchPeriods() {
            if (!this.prestationId) return

            this.loading = true
            try {
                this.periods = await ApiManager.getAllReservationPeriods(this.prestationId)
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },
        async addPeriod() {
            this.validationError = ''

            if (!this.isFormValid) {
                this.validationError = this.$t('experience.step4.reservationPeriods.validationError')
                return
            }

            this.actionLoading = true
            try {
                await ApiManager.createReservationPeriod(this.prestationId, this.form)
                this.resetForm()
                this.showAddForm = false
                await this.fetchPeriods()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error adding period:', error)
                if (error.status === 409) {
                    this.validationError = this.$t('experience.step4.reservationPeriods.conflict')
                } else {
                    this.validationError = this.$t('experience.step4.reservationPeriods.errorAdd')
                }
            } finally {
                this.actionLoading = false
            }
        },
        async updatePeriod(period) {
            this.actionLoading = true
            try {
                await ApiManager.updateReservationPeriod(period.id, {
                    start: this.formatTime(period.start),
                    end: this.formatTime(period.end),
                    active: period.active,
                })
                await this.fetchPeriods()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error updating period:', error)
            } finally {
                this.actionLoading = false
            }
        },
        async toggleActive(period) {
            period.active = !period.active
            await this.updatePeriod(period)
        },
        async deletePeriod(periodId) {
            this.actionLoading = true
            try {
                await ApiManager.deleteReservationPeriod(periodId)
                await this.fetchPeriods()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error deleting period:', error)
            } finally {
                this.actionLoading = false
            }
        },
        resetForm() {
            this.form = {
                start: '',
                end: '',
                active: true,
            }
        },
    },
    watch: {
        prestationId: {
            handler(newVal) {
                if (newVal) {
                    this.fetchPeriods()
                }
            },
            immediate: true,
        },
    },
}
</script>

<style scoped>
input[type='time']::-webkit-calendar-picker-indicator {
    display: none;
}
</style>
